// Путь: D:\QR\QR-WEB\dashboard\src\components\User\BrandStorePage.jsx
// Название: Brand Store Page - страница бренда с обменом на промокод
// Версия: 4.0
// Дата: 24.01.2026

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BrandStorePage.css';

const API_URL = 'https://qr-money.onrender.com/api';

const BrandStorePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [balance, setBalance] = useState(0);
  const [minAmount, setMinAmount] = useState(10); // Динамический минимум
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Promocode flow
  const [showRedeemFlow, setShowRedeemFlow] = useState(false);
  const [redeemStep, setRedeemStep] = useState(1); // 1=profile, 2=email, 3=confirm, 4=success
  const [redeeming, setRedeeming] = useState(false);
  const [receivedPromocode, setReceivedPromocode] = useState(null);
  
  // Profile data
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    email_verified: false
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  useEffect(() => {
    loadBrandData();
  }, [slug]);

  const loadBrandData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/brand-store/brand/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setBrand(data.data.brand);
        setBalance(parseFloat(data.data.balance?.amount || 0));
        
        // Загружаем минимальную сумму промокода
        loadMinAmount(data.data.brand.id);
      } else {
        setMessage({ type: 'error', text: 'Бренд не найден' });
      }
    } catch (err) {
      console.error('Load brand error:', err);
      setMessage({ type: 'error', text: 'Ошибка загрузки' });
    } finally {
      setLoading(false);
    }
  };

  const loadMinAmount = async (brandId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/promocodes/min-amount/${brandId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data.min_amount) {
        setMinAmount(parseFloat(data.data.min_amount));
      }
    } catch (err) {
      console.error('Load min amount error:', err);
      // Оставляем default 10
    }
  };

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      const res = await fetch(`${API_URL}/profile/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setProfile({
          first_name: data.data.first_name || '',
          last_name: data.data.last_name || '',
          phone: data.data.phone || '',
          email: data.data.email || '',
          email_verified: data.data.email_verified || false
        });
        return data.data;
      }
    } catch (err) {
      console.error('Load profile error:', err);
    }
    return null;
  };

  const startRedeemFlow = async () => {
    if (balance < minAmount) {
      setMessage({ type: 'error', text: `Минимум ${minAmount} DAR для обмена` });
      return;
    }
    
    const profileData = await loadProfile();
    
    if (!profileData?.first_name || !profileData?.last_name || !profileData?.phone) {
      setRedeemStep(1); // Profile step
    } else if (!profileData?.email_verified) {
      setRedeemStep(2); // Email step
    } else {
      setRedeemStep(3); // Confirm step
    }
    
    setShowRedeemFlow(true);
  };

  const handleSaveProfile = async () => {
    if (!profile.first_name || !profile.last_name || !profile.phone) {
      setMessage({ type: 'error', text: 'Заполните все поля' });
      return;
    }

    setSavingProfile(true);
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      const res = await fetch(`${API_URL}/profile/${userId}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone
        })
      });
      const data = await res.json();
      
      if (data.success) {
        if (!profile.email_verified) {
          setRedeemStep(2); // Go to email verification
        } else {
          setRedeemStep(3); // Go to confirm
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка сохранения' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    } finally {
      setSavingProfile(false);
    }
  };

  const sendVerificationEmail = async () => {
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Письмо отправлено! Проверьте почту.' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка отправки' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    } finally {
      setSendingEmail(false);
    }
  };

  const checkEmailVerified = async () => {
    setCheckingEmail(true);
    try {
      const profileData = await loadProfile();
      if (profileData?.email_verified) {
        setProfile(prev => ({ ...prev, email_verified: true }));
        setRedeemStep(3); // Go to confirm
        setMessage({ type: 'success', text: 'Email подтверждён!' });
      } else {
        setMessage({ type: 'error', text: 'Email ещё не подтверждён. Проверьте почту.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка проверки' });
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleRedeemPromocode = async () => {
    setRedeeming(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/promocodes/redeem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ brand_id: brand.id })
      });
      const data = await res.json();
      
      if (data.success) {
        setReceivedPromocode({
          code: data.data.promocode,
          dar_amount: data.data.dar_spent
        });
        setBalance(prev => prev - parseFloat(data.data.dar_spent));
        setRedeemStep(4); // Success
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка получения промокода' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    } finally {
      setRedeeming(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Код скопирован!' });
    setTimeout(() => setMessage(null), 2000);
  };

  const closeFlow = () => {
    setShowRedeemFlow(false);
    setRedeemStep(1);
    if (receivedPromocode) {
      setReceivedPromocode(null);
    }
  };

  const getBrandColor = () => {
    if (!brand?.primary_color || brand.primary_color === '#000000') return '#6366F1';
    return brand.primary_color;
  };

  // Вычисляем сколько DAR спишется (максимум из доступных промокодов)
  const getRedeemAmount = () => {
    // Если баланс >= 10 и есть main промокоды - 10 DAR
    // Если баланс >= 5 и < 10 - 5 DAR (referral)
    if (balance >= 10) return 10;
    if (balance >= 5) return 5;
    return minAmount;
  };

  if (loading) {
    return <div className="bsp-loading"><div className="bsp-spinner"></div></div>;
  }

  if (!brand) {
    return (
      <div className="bsp-page">
        <div className="bsp-not-found">
          <h2>Бренд не найден</h2>
          <button onClick={() => navigate('/brand-store')} className="bsp-btn">
            ← К списку брендов
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bsp-page">
      {/* Header */}
      <div className="bsp-header" style={{ borderColor: getBrandColor() }}>
        <div className="bsp-brand-info">
          <span className="bsp-emoji">{brand.logo_emoji || '🎁'}</span>
          <div>
            <h1>{brand.name}</h1>
            {brand.description && <p>{brand.description}</p>}
          </div>
        </div>
        <div className="bsp-balance-card" style={{ background: getBrandColor() }}>
          <span className="bsp-balance-label">Ваш баланс</span>
          <span className="bsp-balance-amount">{balance.toFixed(2)} DAR</span>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`bsp-message bsp-message-${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Redeem Section */}
      <div className="bsp-redeem-section">
        <h2>🎟️ Обменять на промокод</h2>
        <p>Обменяйте ваши DAR на промокод от {brand.name}</p>
        
        <div className="bsp-redeem-info">
          <div className="bsp-redeem-row">
            <span>Ваш баланс:</span>
            <strong>{balance.toFixed(2)} DAR</strong>
          </div>
          <div className="bsp-redeem-row">
            <span>Минимум для обмена:</span>
            <strong>{minAmount} DAR</strong>
          </div>
        </div>

        {balance >= minAmount ? (
          <button 
            className="bsp-redeem-btn"
            style={{ background: getBrandColor() }}
            onClick={startRedeemFlow}
          >
            🎁 Получить промокод
          </button>
        ) : (
          <div className="bsp-redeem-disabled">
            <p>Недостаточно DAR для обмена</p>
            <small>Активируйте подарки чтобы получить больше DAR</small>
          </div>
        )}
      </div>

      {/* Multi-step Redeem Flow Modal */}
      {showRedeemFlow && (
        <div className="bsp-modal-overlay" onClick={closeFlow}>
          <div className="bsp-modal bsp-modal-wide" onClick={e => e.stopPropagation()}>
            
            {/* Progress Steps */}
            <div className="bsp-flow-steps">
              <div className={`bsp-flow-step ${redeemStep >= 1 ? 'active' : ''} ${redeemStep > 1 ? 'done' : ''}`}>
                <span className="bsp-flow-num">{redeemStep > 1 ? '✓' : '1'}</span>
                <span className="bsp-flow-label">Профиль</span>
              </div>
              <div className="bsp-flow-line"></div>
              <div className={`bsp-flow-step ${redeemStep >= 2 ? 'active' : ''} ${redeemStep > 2 ? 'done' : ''}`}>
                <span className="bsp-flow-num">{redeemStep > 2 ? '✓' : '2'}</span>
                <span className="bsp-flow-label">Email</span>
              </div>
              <div className="bsp-flow-line"></div>
              <div className={`bsp-flow-step ${redeemStep >= 3 ? 'active' : ''} ${redeemStep > 3 ? 'done' : ''}`}>
                <span className="bsp-flow-num">{redeemStep > 3 ? '✓' : '3'}</span>
                <span className="bsp-flow-label">Промокод</span>
              </div>
            </div>

            {/* Step 1: Profile */}
            {redeemStep === 1 && (
              <div className="bsp-flow-content">
                <div className="bsp-flow-icon">👤</div>
                <h3>Заполните профиль</h3>
                <p>Для получения промокода укажите ваши данные</p>
                
                <div className="bsp-form">
                  <div className="bsp-form-row">
                    <div className="bsp-form-group">
                      <label>Имя *</label>
                      <input
                        type="text"
                        value={profile.first_name}
                        onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                        placeholder="Введите имя"
                      />
                    </div>
                    <div className="bsp-form-group">
                      <label>Фамилия *</label>
                      <input
                        type="text"
                        value={profile.last_name}
                        onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                        placeholder="Введите фамилию"
                      />
                    </div>
                  </div>
                  <div className="bsp-form-group">
                    <label>Телефон *</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      placeholder="+380..."
                    />
                  </div>
                </div>

                <div className="bsp-modal-actions">
                  <button className="bsp-modal-cancel" onClick={closeFlow}>
                    Отмена
                  </button>
                  <button 
                    className="bsp-modal-confirm"
                    style={{ background: getBrandColor() }}
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? 'Сохранение...' : 'Продолжить →'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Email Verification */}
            {redeemStep === 2 && (
              <div className="bsp-flow-content">
                <div className="bsp-flow-icon">📧</div>
                <h3>Подтвердите email</h3>
                <p>На вашу почту будет отправлено письмо с ссылкой для подтверждения</p>
                
                <div className="bsp-email-box">
                  <span>{profile.email}</span>
                </div>

                <div className="bsp-email-instructions">
                  <p>1. Нажмите "Отправить письмо"</p>
                  <p>2. Откройте почту и перейдите по ссылке</p>
                  <p>3. Вернитесь сюда и нажмите "Проверить"</p>
                </div>

                <div className="bsp-modal-actions bsp-modal-actions-col">
                  <button 
                    className="bsp-modal-confirm"
                    style={{ background: getBrandColor() }}
                    onClick={sendVerificationEmail}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? 'Отправка...' : '📨 Отправить письмо'}
                  </button>
                  <button 
                    className="bsp-modal-confirm"
                    style={{ background: '#10b981' }}
                    onClick={checkEmailVerified}
                    disabled={checkingEmail}
                  >
                    {checkingEmail ? 'Проверка...' : '✓ Я подтвердил, проверить'}
                  </button>
                  <button className="bsp-modal-cancel" onClick={closeFlow}>
                    Отмена
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {redeemStep === 3 && (
              <div className="bsp-flow-content">
                <div className="bsp-flow-icon">🎁</div>
                <h3>Подтверждение обмена</h3>
                <p>Вы хотите обменять <strong>{getRedeemAmount()} DAR</strong> на промокод от {brand.name}?</p>
                
                <div className="bsp-confirm-info">
                  <div className="bsp-confirm-row">
                    <span>👤 Имя:</span>
                    <strong>{profile.first_name} {profile.last_name}</strong>
                  </div>
                  <div className="bsp-confirm-row">
                    <span>📧 Email:</span>
                    <strong>{profile.email} ✓</strong>
                  </div>
                  <div className="bsp-confirm-row">
                    <span>📱 Телефон:</span>
                    <strong>{profile.phone}</strong>
                  </div>
                  <div className="bsp-confirm-row bsp-confirm-highlight">
                    <span>💎 Спишется:</span>
                    <strong>{getRedeemAmount()} DAR</strong>
                  </div>
                </div>

                <div className="bsp-modal-actions">
                  <button className="bsp-modal-cancel" onClick={closeFlow}>
                    Отмена
                  </button>
                  <button 
                    className="bsp-modal-confirm"
                    style={{ background: getBrandColor() }}
                    onClick={handleRedeemPromocode}
                    disabled={redeeming}
                  >
                    {redeeming ? 'Получение...' : '🎉 Получить промокод'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {redeemStep === 4 && receivedPromocode && (
              <div className="bsp-flow-content bsp-flow-success">
                <div className="bsp-flow-icon">🎉</div>
                <h3>Поздравляем!</h3>
                <p>Вы получили промокод:</p>
                
                <div className="bsp-promocode-display">
                  <span className="bsp-promocode-text">{receivedPromocode.code}</span>
                  <button 
                    className="bsp-copy-btn"
                    onClick={() => copyToClipboard(receivedPromocode.code)}
                  >
                    📋 Копировать
                  </button>
                </div>

                <p className="bsp-promocode-value">Номинал: {receivedPromocode.dar_amount} DAR</p>

                <div className="bsp-modal-actions">
                  <button 
                    className="bsp-modal-confirm"
                    style={{ background: getBrandColor() }}
                    onClick={() => navigate('/promocodes')}
                  >
                    Мои промокоды →
                  </button>
                  <button className="bsp-modal-cancel" onClick={closeFlow}>
                    Закрыть
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bsp-footer">
        <button onClick={() => navigate('/brand-store')} className="bsp-back-btn">
          ← Все бренды
        </button>
        <button onClick={() => navigate('/dashboard')} className="bsp-back-btn">
          🏠 На главную
        </button>
      </div>
    </div>
  );
};

export default BrandStorePage;